/**
 * Export-zip → candidate NamedText[]. Runs in the browser (jszip loads
 * lazily inside the call so bundlers keep it out of the main graph) and in
 * node tests. Only memory-bearing names come out; conversations and binaries
 * never leave the zip.
 */
import { IMPORT_FILE_MAX_BYTES, IMPORT_MAX_FILES, type NamedText } from './types.js';

const CANDIDATE_NAME = /\.(md|mdc|markdown|txt|json)$/i;
const CANDIDATE_BARE_NAMES = new Set(['.cursorrules']);
const EXCLUDED_BASENAMES = new Set(['conversations.json', 'chat.html', 'shared_conversations.json', 'message_feedback.json', 'model_comparisons.json']);

export interface ZipCandidates {
  files: NamedText[];
  /** Entry names that were present but not extracted (excluded, oversize, or overflow). */
  skipped: string[];
}

export async function extractMemoryCandidates(data: Uint8Array | ArrayBuffer): Promise<ZipCandidates> {
  const { default: JSZip } = await import('jszip');
  const zip = await JSZip.loadAsync(data);
  const files: NamedText[] = [];
  const skipped: string[] = [];

  const entries = Object.values(zip.files).filter((e) => !e.dir);
  for (const entry of entries) {
    const base = entry.name.split('/').pop()?.toLowerCase() ?? entry.name.toLowerCase();
    const isCandidate = (CANDIDATE_NAME.test(base) || CANDIDATE_BARE_NAMES.has(base)) && !EXCLUDED_BASENAMES.has(base);
    if (!isCandidate) {
      skipped.push(entry.name);
      continue;
    }
    if (files.length >= IMPORT_MAX_FILES) {
      skipped.push(entry.name);
      continue;
    }
    const text = await entry.async('string');
    if (text.length > IMPORT_FILE_MAX_BYTES) {
      skipped.push(entry.name);
      continue;
    }
    files.push({ name: entry.name, text });
  }
  return { files, skipped };
}
